import jsPDF from "jspdf";
import cfgLogo from "@/assets/cfg-logo.png";

export interface LandContractAgreementData {
  primarySellerName: string;
  secondarySellerName?: string;
  propertyAddress: string;
  balance: string;
  firstPaymentDate: string;
  dateOfBalance: string;
  date: Date | undefined;
  agreementType: "partial" | "full" | "private";
  paperType?: string;
  partialDollarAmount?: string;
  brokerOfferToSeller?: string;
  partialEndDate?: string;
  buyerName?: string;
  purchaserName?: string;
  contractDate?: string;
  interestRate?: string;
  nextPaymentDue?: string;
  purchaseAmount?: string;
  liquidatedDamages?: string;
  draftedBy?: string;
  referenceNumber?: string;
}

// ---------- helpers ----------
const fmtCurrency = (v: string | number | undefined): string => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/[,$]/g, ""));
  if (!v || isNaN(n)) return "$________________";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

const fmtDate = (v: string | Date | undefined): string => {
  if (!v) return "________________";
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

const orBlank = (v: string | undefined) => (v && v.trim() ? v : "________________");

// Preload logo once (base64 data URL + intrinsic dimensions)
// Uses Image + canvas to avoid any Buffer/Node polyfill paths in the browser.
let logoCache: { dataUrl: string; w: number; h: number } | null = null;
const loadLogo = async (): Promise<typeof logoCache> => {
  if (logoCache) return logoCache;
  try {
    const img: HTMLImageElement = await new Promise((resolve, reject) => {
      const el = new Image();
      el.crossOrigin = "anonymous";
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = cfgLogo;
    });
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0);
    const dataUrl = canvas.toDataURL("image/png");
    logoCache = { dataUrl, w: img.naturalWidth, h: img.naturalHeight };
    return logoCache;
  } catch (e) {
    console.error("Error loading logo:", e);
    return null;
  }
};

// ---------- layout ----------
const PAGE = { w: 8.5, h: 11 };
const MARGIN = { top: 0.6, bottom: 0.75, left: 0.85, right: 0.85 };
const CONTENT_W = PAGE.w - MARGIN.left - MARGIN.right;
const BODY_FONT_SIZE = 11;
const LINE_HEIGHT = 0.19; // inches

type Block =
  | { kind: "para"; text: string; bold?: boolean; align?: "left" | "center"; size?: number; spaceAfter?: number; indent?: number }
  | { kind: "space"; size: number }
  | { kind: "rule" }
  | { kind: "signatures"; primary: string; secondary?: string; buyer: string };

// Build content blocks per agreement type
function buildBlocks(d: LandContractAgreementData): Block[] {
  const sellerNames = d.secondarySellerName
    ? `${d.primarySellerName} and ${d.secondarySellerName}`
    : d.primarySellerName;
  const buyer = d.buyerName || "Contract Funding Group, LLC";
  const paper = d.paperType || "Land Contract";

  const blocks: Block[] = [];

  // Title
  const titleText =
    d.agreementType === "private"
      ? "LAND CONTRACT PURCHASE & SALE AGREEMENT"
      : d.agreementType === "partial"
      ? "PARTIAL PURCHASE AGREEMENT"
      : "LAND CONTRACT PURCHASE AGREEMENT";

  blocks.push({ kind: "para", text: titleText, bold: true, align: "center", size: 14, spaceAfter: 0.05 });
  blocks.push({
    kind: "para",
    text: `Dated ${fmtDate(d.date)}`,
    align: "center",
    size: 10,
    spaceAfter: 0.15,
  });
  blocks.push({ kind: "rule" });
  blocks.push({ kind: "space", size: 0.15 });

  if (d.agreementType === "private") {
    blocks.push({
      kind: "para",
      text:
        `This Agreement is entered into between ${sellerNames} ("Seller"), and ${buyer} ("Buyer"), ` +
        `for the purchase of the Seller's interest in the Land Contract identified below.`,
      spaceAfter: 0.15,
    });
    blocks.push({ kind: "para", text: "1. LAND CONTRACT IDENTIFICATION.", bold: true, spaceAfter: 0.05 });
    blocks.push({
      kind: "para",
      text:
        `Purchaser under the Land Contract: ${orBlank(d.purchaserName)}. ` +
        `Contract dated ${fmtDate(d.contractDate)}. ` +
        `Current unpaid balance of ${fmtCurrency(d.balance)} as of ${fmtDate(d.dateOfBalance) || fmtDate(new Date())}. ` +
        `Interest rate: ${orBlank(d.interestRate)}%. ` +
        `Secured by real property located at ${orBlank(d.propertyAddress)}.`,
      spaceAfter: 0.15,
    });
    blocks.push({ kind: "para", text: "2. PURCHASE AND SALE.", bold: true, spaceAfter: 0.05 });
    blocks.push({
      kind: "para",
      text:
        `Seller agrees to sell and Buyer agrees to purchase all remaining payments due under the Land Contract, ` +
        `beginning with the payment due ${fmtDate(d.nextPaymentDue)}. ` +
        `Buyer shall pay Seller ${fmtCurrency(d.purchaseAmount || d.brokerOfferToSeller)} at closing, per the final closing statement.`,
      spaceAfter: 0.15,
    });
    blocks.push({ kind: "para", text: "3. SELLER WARRANTIES.", bold: true, spaceAfter: 0.05 });
    blocks.push({
      kind: "para",
      text:
        "Seller warrants: (a) all information provided is true and correct; " +
        "(b) Seller holds clear, marketable title to the Land Contract and can convey same to Buyer; " +
        "(c) the Land Contract represents a bona fide sale of real estate and is binding as of its date; " +
        "(d) Seller has no knowledge of any defenses, offsets, or claims affecting payment; and " +
        "(e) the Land Contract complies with all applicable federal and state truth-in-lending, consumer protection, and usury laws.",
      spaceAfter: 0.15,
    });
    blocks.push({ kind: "para", text: "4. SELLER CANCELLATION.", bold: true, spaceAfter: 0.05 });
    blocks.push({
      kind: "para",
      text: `If Seller elects to cancel this Agreement or fails to comply with standard closing procedures, Seller shall be liable to Buyer for ${fmtCurrency(d.liquidatedDamages || "500")} as liquidated damages.`,
      spaceAfter: 0.15,
    });
    blocks.push({ kind: "para", text: "5. BUYER RIGHT TO CANCEL.", bold: true, spaceAfter: 0.05 });
    blocks.push({
      kind: "para",
      text:
        "Buyer may cancel this Agreement at its sole discretion if the Purchaser's credit, payment history, title, or property appraisal is deemed insufficient.",
      spaceAfter: 0.15,
    });
    blocks.push({ kind: "para", text: "6. ENTIRE AGREEMENT.", bold: true, spaceAfter: 0.05 });
    blocks.push({
      kind: "para",
      text:
        "This Agreement is the entire agreement between the parties and may be amended only by a written instrument signed by an authorized representative of Buyer.",
      spaceAfter: 0.15,
    });
    blocks.push({ kind: "para", text: "7. TIME OF THE ESSENCE.", bold: true, spaceAfter: 0.05 });
    blocks.push({
      kind: "para",
      text:
        "Time is of the essence. Seller acknowledges that this transaction is the purchase of a stream of payments and is not a loan.",
      spaceAfter: 0.2,
    });
  } else {
    const isPartial = d.agreementType === "partial";
    blocks.push({
      kind: "para",
      text:
        `This Purchase Agreement is made and entered into on ${fmtDate(d.date)}, by and between ` +
        `${sellerNames} ("Seller") and ${buyer} and/or assigns ("Purchaser"), ` +
        `whereas Seller is the owner of a certain ${paper}.`,
      spaceAfter: 0.12,
    });
    blocks.push({
      kind: "para",
      text: `Property address: ${orBlank(d.propertyAddress)}.`,
      spaceAfter: 0.12,
    });
    blocks.push({
      kind: "para",
      text:
        `Seller agrees to transfer said ${paper} upon the terms and conditions set forth herein. ` +
        `In consideration of the mutual benefits derived, Seller grants Purchaser, and/or assigns, the exclusive right to purchase the note and security instrument, and the parties agree as follows:`,
      spaceAfter: 0.18,
    });

    blocks.push({
      kind: "para",
      text: `1. PURCHASE PRICE. Seller warrants and represents that the unpaid balance on the above ${paper} as of ${fmtDate(d.dateOfBalance)} is ${fmtCurrency(d.balance)}.`,
      spaceAfter: 0.12,
    });

    if (isPartial) {
      blocks.push({
        kind: "para",
        text:
          `2. PARTIAL PURCHASE. Purchaser agrees to buy the next ${fmtCurrency(d.partialDollarAmount)} of payments due, and Seller agrees to sell said payments for ${fmtCurrency(d.brokerOfferToSeller)}, ` +
          `with payments to Purchaser beginning ${fmtDate(d.firstPaymentDate)} and ending with the payment due ${fmtDate(d.partialEndDate)}, assuming no unpaid fees or costs accumulated by the borrowers.`,
        spaceAfter: 0.12,
      });
    } else {
      blocks.push({
        kind: "para",
        text:
          `2. FULL PURCHASE. Purchaser agrees to buy the entire remaining balance of ${fmtCurrency(d.balance)} and shall pay Seller ${fmtCurrency(d.brokerOfferToSeller)} for the full purchase of said ${paper}, ` +
          `with payments to Purchaser beginning ${fmtDate(d.firstPaymentDate)}.`,
        spaceAfter: 0.12,
      });
    }

    blocks.push({ kind: "para", text: "3. COSTS. Purchaser will pay title and appraisal costs.", spaceAfter: 0.1 });
    blocks.push({
      kind: "para",
      text: "4. TAXES. Seller warrants that all real property taxes due and payable have been paid.",
      spaceAfter: 0.1,
    });
    blocks.push({
      kind: "para",
      text: "5. CONTINGENCIES. This purchase is expressly contingent upon the following conditions:",
      spaceAfter: 0.06,
    });
    blocks.push({
      kind: "para",
      text: `A. The real property securing the above ${paper} shall be of sufficient value to secure the investment, as determined in the sole discretion of Purchaser.`,
      indent: 0.25,
      spaceAfter: 0.06,
    });
    blocks.push({
      kind: "para",
      text: `B. Purchaser shall have 30 days to fully examine Seller's title, the ${paper}, the property, the payment history, and the credit and financial status of the payers.`,
      indent: 0.25,
      spaceAfter: 0.12,
    });
    blocks.push({
      kind: "para",
      text:
        "6. CANCELLATION. If any condition of sale is found unacceptable to Purchaser, Purchaser shall notify Seller and this Agreement shall become null and void.",
      spaceAfter: 0.1,
    });
    blocks.push({
      kind: "para",
      text: `7. RECORDS. Seller shall keep and provide documentation of any ${paper} payments received during processing of this transaction.`,
      spaceAfter: 0.1,
    });
    blocks.push({
      kind: "para",
      text:
        "8. TERM. This Agreement shall remain in full force and effect for a period of 30 days from the date Purchaser receives all requested documentation.",
      spaceAfter: 0.2,
    });
  }

  blocks.push({
    kind: "signatures",
    primary: d.primarySellerName || "Seller",
    secondary: d.secondarySellerName,
    buyer,
  });

  if (d.draftedBy || d.referenceNumber) {
    blocks.push({
      kind: "para",
      text: `Drafted by: ${orBlank(d.draftedBy)}   |   Reference: ${orBlank(d.referenceNumber)}`,
      size: 9,
      align: "center",
      spaceAfter: 0,
    });
  }

  return blocks;
}

// ---------- renderer ----------
export async function generateLandContractPurchaseAgreementPdf(
  d: LandContractAgreementData
): Promise<Blob> {
  const doc = new jsPDF({ unit: "in", format: "letter" });
  const logo = await loadLogo();

  const drawHeader = (isFirst: boolean) => {
    if (!logo) return MARGIN.top;
    const targetW = isFirst ? 1.6 : 1.0;
    const targetH = (logo.h / logo.w) * targetW;
    const x = (PAGE.w - targetW) / 2;
    const y = MARGIN.top;
    doc.addImage(logo.dataUrl, "PNG", x, y, targetW, targetH);
    const bottom = y + targetH + 0.08;
    // divider line
    doc.setDrawColor(139, 69, 19); // subtle brown
    doc.setLineWidth(0.01);
    doc.line(MARGIN.left, bottom, PAGE.w - MARGIN.right, bottom);
    return bottom + 0.15;
  };

  let cursorY = drawHeader(true);
  let pageIndex = 0;

  const ensureSpace = (needed: number) => {
    if (cursorY + needed > PAGE.h - MARGIN.bottom) {
      doc.addPage();
      pageIndex += 1;
      cursorY = drawHeader(false);
    }
  };

  const drawParagraph = (b: Extract<Block, { kind: "para" }>) => {
    const size = b.size ?? BODY_FONT_SIZE;
    doc.setFont("times", b.bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(20, 20, 20);
    const indent = b.indent ?? 0;
    const width = CONTENT_W - indent;
    const lines = doc.splitTextToSize(b.text, width) as string[];
    const lineH = size * 0.014 + 0.055; // approx inches per line
    for (const line of lines) {
      ensureSpace(lineH);
      let x = MARGIN.left + indent;
      if (b.align === "center") {
        const tw = doc.getTextWidth(line);
        x = (PAGE.w - tw) / 2;
      }
      doc.text(line, x, cursorY);
      cursorY += lineH;
    }
    cursorY += b.spaceAfter ?? 0.08;
  };

  const drawRule = () => {
    ensureSpace(0.05);
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.008);
    doc.line(MARGIN.left, cursorY, PAGE.w - MARGIN.right, cursorY);
    cursorY += 0.05;
  };

  const drawSignatures = (b: Extract<Block, { kind: "signatures" }>) => {
    const rowsNeeded = b.secondary ? 3 : 2;
    const blockH = rowsNeeded * 0.55 + 0.25;
    ensureSpace(blockH);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(20, 20, 20);
    doc.text("SELLER", MARGIN.left, cursorY);
    doc.text("PURCHASER", MARGIN.left + CONTENT_W / 2 + 0.15, cursorY);
    cursorY += 0.35;

    const drawSigRow = (leftName: string, rightName: string) => {
      const colW = CONTENT_W / 2 - 0.15;
      const leftX = MARGIN.left;
      const rightX = MARGIN.left + CONTENT_W / 2 + 0.15;
      // signature lines
      doc.setDrawColor(60, 60, 60);
      doc.setLineWidth(0.01);
      doc.line(leftX, cursorY, leftX + colW, cursorY);
      doc.line(rightX, cursorY, rightX + colW, cursorY);
      // labels below lines
      doc.setFont("times", "normal");
      doc.setFontSize(10);
      doc.text(leftName, leftX, cursorY + 0.18);
      doc.text(rightName, rightX, cursorY + 0.18);
      // date line
      doc.setFont("times", "italic");
      doc.setFontSize(9);
      doc.setTextColor(90, 90, 90);
      doc.text(`Date: _______________________`, leftX, cursorY + 0.36);
      doc.text(`Date: _______________________`, rightX, cursorY + 0.36);
      doc.setTextColor(20, 20, 20);
      cursorY += 0.55;
    };

    drawSigRow(b.primary, b.buyer);
    if (b.secondary) drawSigRow(b.secondary, "");
    cursorY += 0.15;
  };

  const blocks = buildBlocks(d);
  for (const b of blocks) {
    if (b.kind === "para") drawParagraph(b);
    else if (b.kind === "space") {
      ensureSpace(b.size);
      cursorY += b.size;
    } else if (b.kind === "rule") drawRule();
    else if (b.kind === "signatures") drawSignatures(b);
  }

  // Footer on every page
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(
      "Contract Funding Group, LLC  •  info@contractfundinggroup.com",
      PAGE.w / 2,
      PAGE.h - 0.35,
      { align: "center" }
    );
    doc.text(`Page ${i} of ${totalPages}`, PAGE.w - MARGIN.right, PAGE.h - 0.35, { align: "right" });
  }

  return doc.output("blob");
}
